import { GameState, Player, PlayerPosition, PokerAction, RangeData, ALL_POSITIONS, Card, Rank, Suit, ActionsTotalFrequencies, TrainingStats, PlayerStatus } from './types';
import { getInterpolatedRange, generateRandomHand, getSortedFullPokerHandArray } from './card-range';
import { DEAL_ANIMATION_MAX_MS } from './shared_constants';

// Convert card objects to notation format for range lookup
export function handToNotation(cards: Card[]): string {
   if (cards.length !== 2) return '';

   const [card1, card2] = cards;

   // If ranks are the same, it's a pair
   if (card1.rank === card2.rank) {
      return card1.rank + card2.rank;
   }

   // Determine rank order (higher rank first)
   const ranks = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
   const card1Index = ranks.indexOf(card1.rank);
   const card2Index = ranks.indexOf(card2.rank);

   let orderedRanks: string;
   if (card1Index <= card2Index) {
      orderedRanks = card1.rank + card2.rank;
   } else {
      orderedRanks = card2.rank + card1.rank;
   }

   // Determine suited or offsuit
   const suited = card1.suit === card2.suit ? 's' : 'o';

   return orderedRanks + suited;
}

export class PokerGame {
   private state: GameState;
   // private actionsToDrill: Set<PokerAction>;
   // private positionsToDrill: Set<PlayerPosition>;
   private onStateChange: (state: GameState) => void;
   private PLAYER_ACTION_SPEED_MS: number = 100; // Will be modified by settings
   private dealAnimationDuration: number = 1500; // Duration of the card dealing animation in milliseconds

   constructor(
      userPosition: PlayerPosition | null, // null implies randomize
      actionsToDrill: PokerAction[],
      onStateChange: (state: GameState) => void
   ) {
      // this.actionsToDrill = new Set(actionsToDrill);
      // this.positionsToDrill = new Set([userPosition]);
      this.onStateChange = onStateChange;

      // Initialize with empty game state
      this.state = {
         players: [],
         userPosition,
         randomizeUserPosition: userPosition === null,
         openerPosition: null,
         threeBetPosition: null,
         potSize: 0,
         currentPlayerIndex: 0,
         userHand: [],
         difficultyMode: 'Simplified',
         playerActions: [],
         currentCorrectActions: [],
         isHandComplete: false,
         ranges: {},
         raiseRanges: {},
         fourBetRanges: {},
         generatedHandRanges: {} as Record<PlayerPosition, string[]>,
         deck: [],
         handRangeWidth: 0,
         stats: {
            totalActions: 0,
            correctDecisions: 0,
            roundNumber: 0,
            roundsTotal: 0 // 10
         }
      };

   }

   private isEdgeCase(f: ActionsTotalFrequencies) {
      const MIN_EDGE_CASE = this.state.difficultyMode === 'Hard' ? 0.01 : 0.2;
      const MAX_EDGE_CASE = 1.0 - MIN_EDGE_CASE;
      const isRangeEdgeCase = Object.entries(f)
         .filter(([k, v]) => /^R\d+(\.\d+)?$/.test(k))
         .some(([_, v]) => typeof v === 'number' && v > MIN_EDGE_CASE && v < MAX_EDGE_CASE);
      if (
         (f.C > MIN_EDGE_CASE && f.C < MAX_EDGE_CASE) || 
            (f.F > MIN_EDGE_CASE && f.F < MAX_EDGE_CASE) ||
            (f.RAI > MIN_EDGE_CASE && f.RAI < MAX_EDGE_CASE) ||
            isRangeEdgeCase
      ) {
         return true;
      }
   }

   public setRangeData(ranges: Record<string, RangeData>, raiseRanges: Record<string, Record<string, RangeData>>, fourBetRanges: Record<string, Record<string, RangeData>>) {
      this.state.ranges = ranges;
      this.state.raiseRanges = raiseRanges;
      this.state.fourBetRanges = fourBetRanges;

      for (const pos in ranges) {
         const positionHandRanges: string[] = [];
         for (const hand in ranges[pos]) {
            for (const k in ranges[pos][hand]) {
               if (this.isEdgeCase(ranges[pos][hand][k])) {
                  positionHandRanges.push(hand);
                  break; // Once we find an edge case for this hand, no need to check other keys
               }
            }
         }
         this.state.generatedHandRanges[pos as PlayerPosition] = positionHandRanges;
      }
      // console.log("------------");
      // console.log(this.state.generatedHandRanges);
      // for (const pos in this.state.generatedHandRanges) {
      //    const handRange = this.state.generatedHandRanges[pos as PlayerPosition];
      //    const sortedHands = getSortedFullPokerHandArray(handRange);
      //    console.log("SORTED:");
      //    console.log(sortedHands);
      // }
   }


   public startNewHand(): void {
      console.log("startNewHand()");
      // Reset game state
      const players: Player[] = [];
      
      // Set a unique handId for this hand to help the renderer detect when a new hand starts
      this.state.handId = Date.now();

      if (this.state.randomizeUserPosition) {
         this.state.userPosition = ALL_POSITIONS[Math.floor(Math.random() * ALL_POSITIONS.length)];
         console.log("Randomized user position: " + this.state.userPosition);
      }

      // Create players in all 9 positions
      for (let i = 0; i < ALL_POSITIONS.length; i++) {
         const position = ALL_POSITIONS[i];
         players.push({
            position,
            isUser: position === this.state.userPosition,
            hand: undefined,
            status: 'active',
            chipStack: 100, // Everyone starts with 100 BB
            betAmount: 0
         });
      }

      // Add blinds
      const sbIndex = players.findIndex(p => p.position === 'SB');
      const bbIndex = players.findIndex(p => p.position === 'BB');

      if (sbIndex >= 0) {
         players[sbIndex].betAmount = 0.5; // Small blind
      }

      if (bbIndex >= 0) {
         players[bbIndex].betAmount = 1; // Big blind
      }

      // Set the pot size
      const potSize = 1.5; // SB + BB

      // Initialize a fresh deck
      const deck = this.initializeDeck();

      // Start with UTG
      const startIndex = (bbIndex + 1) % players.length;

      this.state.players = players;
      this.state.potSize = potSize;
      this.state.currentPlayerIndex = startIndex;
      this.state.userHand = [];
      this.state.playerActions = [];
      this.state.currentCorrectActions = [];
      this.state.isHandComplete = false;
      this.state.deck = deck;
      this.state.openerPosition = null;
      this.state.threeBetPosition = null

      // Deal cards to all players
      this.dealCards();

      // Notify state change to trigger card dealing animation
      this.notifyStateChange("startNewHand()");

      // If it's not the user's turn, proceed with AI actions after a delay to allow the animation to complete
      if (players[startIndex].isUser) {
         this.determineCorrectUserActions();
      } else {
         // Add a bit more time to account for the animation duration
         const aiActionDelay = this.PLAYER_ACTION_SPEED_MS + this.dealAnimationDuration;
         setTimeout(() => this.performAIAction(), aiActionDelay);
      }
   }

   private notifyStateChange(source: string): void {
      // console.log("notifyStateChange (" + source + ")");
      this.onStateChange(this.state); // updateGameDisplay in index.ts
   }

   private initializeDeck(): Card[] {
      const ranks: Rank[] = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
      const suits: Suit[] = ['H', 'D', 'C', 'S'];
      const deck: Card[] = [];
      
      // Create a standard 52-card deck
      for (const suit of suits) {
         for (const rank of ranks) {
            deck.push({ rank, suit });
         }
      }
      
      // Shuffle the deck
      for (let i = deck.length - 1; i > 0; i--) {
         const j = Math.floor(Math.random() * (i + 1));
         [deck[i], deck[j]] = [deck[j], deck[i]]; // Swap elements
      }
      
      return deck;
   }
   
   private dealCards(): void {
      // Deal cards to active players
      const activePlayers = this.state.players.filter(p => p.status !== 'folded');
      
      // Each player gets 2 cards from the allowed range
      for (const player of activePlayers) {
         // Get the current hand range based on the slider value
         const allowedRange = getInterpolatedRange(this.state.handRangeWidth, this.state.generatedHandRanges[player.position]);

         console.log("--- allowedRange for " + player.position);
         console.log(allowedRange);

         // Generate a random hand from the allowed range
         player.hand = generateRandomHand(allowedRange, this.state.deck);
      }
      
      // Store user's hand separately for easy access
      const userPlayer = this.state.players.find(p => p.isUser);
      if (userPlayer && userPlayer.hand) {
         this.state.userHand = [...userPlayer.hand];
      }
   }
   
   private determineCorrectUserActions(): void {
      const userPlayer = this.state.players.find(p => p.isUser);
      if (!userPlayer || !userPlayer.hand) return;

      const handNotation = handToNotation(userPlayer.hand);
      
      // Check if there's been a raise before the user's turn
      const someoneRaised = this.checkForPriorRaise();
      const itsA4Bet = this.checkFor4Bet();

      const sortedBettingPlayers = this.state.players.slice().sort((a, b) => a.betAmount - b.betAmount);
      const threeBettingPlayer = sortedBettingPlayers.pop();
      const openingPlayer = sortedBettingPlayers.pop();
      
      if (itsA4Bet && threeBettingPlayer !== undefined && openingPlayer !== undefined && openingPlayer.isUser) {

         // raisePos would be Opening Player
         // userPos would be the Three Betting Player ?
         // then grab the <three-betting>_4bet.json

         const openingPlayerPos = openingPlayer.position;
         const threeBettingPlayerPos = threeBettingPlayer.position;

         if (!this.state.raiseRanges[openingPlayerPos] || !this.state.raiseRanges[openingPlayerPos][threeBettingPlayerPos]) {
            console.warn(`4BET: No raise range data found for ${openingPlayerPos} raising against ${threeBettingPlayerPos}`);
            console.log(this.state.raiseRanges);
            console.log(this.state.raiseRanges[openingPlayerPos] ?? 'no [openingPlayerPos]')
            console.log(this.state.raiseRanges[openingPlayerPos][threeBettingPlayerPos] ?? 'no [openingPlayerPos][threeBettingPlayerPos]')
            this.endHand();
            return;
         }

         const userRaiseRanges = this.state.fourBetRanges[openingPlayerPos][threeBettingPlayerPos];
         // NOTE: Action on the openingPlayerPos

         if (!userRaiseRanges[handNotation]) {
            console.warn(`4BET: No raise range data found for ${handNotation} when ${threeBettingPlayerPos} raises against an opening of ${openingPlayerPos}`);
            this.endHand();
            return;
         }

         const handData = userRaiseRanges[handNotation];
         const frequencies = handData.f;

         // Determine the correct actions based on frequencies for post-raise scenario
         this.calculateCorrectActionsFromFrequencies(frequencies);
      } else if (someoneRaised) {

         if (itsA4Bet) {
            console.warn("Its a 4bet, but the user does not have the action. Hmm...");
            this.endHand();
            return;
         }

         // There was a BET
         // could be AI, could be player

         // Get the player who raised
         const raisingAIPlayer = this.findRaisingAIPlayer();
         if (!raisingAIPlayer) {
            console.warn('RAISE: No raising player found even though a raise was detected');
            this.endHand();
            return;
         }
         
         // Get the raise ranges for this scenario (raising position -> user position)
         const raisePos = raisingAIPlayer.position;
         const userPos = userPlayer.position;
         
         if (!this.state.raiseRanges[raisePos] || !this.state.raiseRanges[raisePos][userPos]) {
            console.warn(`RAISE: No raise range data found for ${raisePos} raising against ${userPos}`);
            console.log(this.state.raiseRanges);
            console.log(this.state.raiseRanges[raisePos] ?? 'no [raisePos]')
            console.log(this.state.raiseRanges[raisePos][userPos] ?? 'no [raisePos][userPos]')
            this.endHand();
            return;
         }
         
         const userRaiseRanges = this.state.raiseRanges[raisePos][userPos];
         
         if (!userRaiseRanges[handNotation]) {
            console.warn(`RAISE: No raise range data found for ${handNotation} when ${raisePos} OPENS against ${userPos}`);
            this.endHand();
            return;
         }
         
         const handData = userRaiseRanges[handNotation];
         const frequencies = handData.f;
         
         // Determine the correct actions based on frequencies for post-raise scenario
         this.calculateCorrectActionsFromFrequencies(frequencies);
      } else {
         // Use standard ranges when there's no raise before the user
         const userRanges = this.state.ranges[userPlayer.position];
         
         if (!userRanges || !userRanges[handNotation]) {
            console.log('this.state.ranges:');
            console.log(this.state.ranges);
            console.warn(`OPENING: No range data found for ${handNotation} in position ${userPlayer.position}`);
            this.endHand();
            return;
         }
         
         const handData = userRanges[handNotation];
         const frequencies = handData.f;
         
         // Calculate correct actions from frequencies
         this.calculateCorrectActionsFromFrequencies(frequencies);
      }
   }
   
   private calculateCorrectActionsFromFrequencies(frequencies: ActionsTotalFrequencies): void {
      // Determine the correct actions based on frequencies
      const actions: PokerAction[] = [];
      const threshold = 0; // Minimum frequency to consider an action valid

      if (frequencies.F > threshold) actions.push('F');
      if (frequencies.C > threshold) actions.push('C');
      if (frequencies.RAI > threshold) actions.push('RAI');

      if (this.state.difficultyMode === 'Simplified') {
         // consolidate all of the Rx.x frequencies into just a simple 3x raise
         if (
            Object.keys(frequencies).some(
               k => /^R(?!AI)/.test(k) && k in frequencies && frequencies[k as keyof ActionsTotalFrequencies] !== undefined && frequencies[k as keyof ActionsTotalFrequencies]! > threshold
            )
         ) {
            actions.push('R3');
         }        
      } else if (this.state.difficultyMode === 'Hard') {
         // add all the Rx.x frequencies to the actions which pass the threshold
         Object.keys(frequencies).forEach(key => {
           if (key.startsWith('R') && key !== 'RAI' && key in frequencies && frequencies[key as keyof ActionsTotalFrequencies] !== undefined && frequencies[key as keyof ActionsTotalFrequencies]! > threshold) {
             actions.push(key as keyof ActionsTotalFrequencies);
           }
         });
      }

      this.state.currentCorrectActions = actions;
   }
   
   private checkForPriorAIRaise(): boolean {
      // Check if any AI player has raised before the user's turn
      const raisedPlayers = this.state.players.filter(p => 
         !p.isUser && (p.status === 'raised' || p.status === 'all-in')
      );
      
      return raisedPlayers.length > 0;
   }

   private checkFor4Bet(): boolean {
      // Check if any player has raised before the user's turn
      const raisedPlayers = this.state.players.filter(p => 
         (p.status === 'raised' || p.status === 'all-in')
      );

      return raisedPlayers.length === 2;
   }

   private checkForPriorRaise(): boolean {
      // Check if any player has raised before the user's turn
      const raisedPlayers = this.state.players.filter(p => 
         (p.status === 'raised' || p.status === 'all-in')
      );

      return raisedPlayers.length > 0;
   }
   
   private findRaisingAIPlayer(): Player | undefined {
      // Find the AI player who raised first
      return this.state.players.find(p => 
         !p.isUser && (p.status === 'raised' || p.status === 'all-in')
      );
   }

   private updateStatePlayerPositions(position: PlayerPosition, status: PlayerStatus) {
      console.log("player pos: " + position + " status: " + status);
      if (status === 'raised' || status === 'all-in') {
         if (this.state.openerPosition === null) {
            this.state.openerPosition = position;
            console.log('setting this.state.openerPosition to: ', this.state.openerPosition);
            return;
         } else if (this.state.threeBetPosition === null) {
            this.state.threeBetPosition = position;
            console.log('setting this.state.threeBetPosition to: ', this.state.threeBetPosition);
            return;
         }
      }
   }

   public makeUserAction(action: PokerAction) {
      console.log("Making user action: " + action);
      this.state.playerActions.push({action, actor: 'USER'});
      const isCorrect = this.state.currentCorrectActions.includes(action); // TODO check currentCorrectActions
      const userIndex = this.state.players.findIndex(p => p.isUser);

      // Update training stats
      this.state.stats.totalActions++;
      if (isCorrect) {
         this.state.stats.correctDecisions++;
      }

      if (userIndex === -1) return false;

      // Update player state based on action
      const player = this.state.players[userIndex];

      switch (action) {
         case 'F':
            player.status = 'folded';
            break;
         case 'C':
            player.status = 'called';
            // Find the current highest bet
            const highestBet = Math.max(...this.state.players.map(p => p.betAmount));
            player.betAmount = Math.min(highestBet, player.chipStack);
            player.chipStack -= player.betAmount;
            this.state.potSize += player.betAmount;
            break;
         case 'R3': // TODO update with more if hard difficulty
            player.status = 'raised';
            // 3-bet is typically 3x the current bet
            const currentBet = Math.max(...this.state.players.map(p => p.betAmount));
            player.betAmount = Math.min(currentBet * 3, player.chipStack);
            player.chipStack -= player.betAmount;
            this.state.potSize += player.betAmount;
            break;
         case 'RAI':
            player.status = 'all-in';
            this.state.potSize += player.chipStack;
            player.betAmount = player.chipStack;
            player.chipStack = 0;
            break;
      }

      this.updateStatePlayerPositions(player.position, player.status);

      // Move to the next player
      this.moveToNextPlayer();

      if (this.shouldEndHand()) {
         this.endHand();
         return;
      } else if (!this.state.players[this.state.currentPlayerIndex].isUser) {
         // Let AI play next
         setTimeout(() => this.performAIAction(), this.PLAYER_ACTION_SPEED_MS);
      }

      this.notifyStateChange("makeUserAction");
   }

   private performAIAction(): void {
      // console.log("performAIAction()");
      const aiPlayer = this.state.players[this.state.currentPlayerIndex];

      const currentHighestBetAnyone = Math.max(...this.state.players.map(p => p.betAmount));
      const currentHighestAIBet = Math.max(...this.state.players.map(p => !p.isUser ? p.betAmount : 0));
      const anAIHasBetAlready = currentHighestAIBet > 1 ? true : false; // more than 1 BB?
      const nobodyHasBetYet = currentHighestBetAnyone > 1 ? false : true; // 1 BB
      
      if (!aiPlayer.hand) return;

      if (!anAIHasBetAlready) { // if no AI bet yet

         const positionRanges = this.state.ranges[aiPlayer.position];
         const userPlayer = this.state.players.find(p => p.isUser);
         if (!userPlayer) return;
         // console.log("this.state.raiseRanges:", this.state.raiseRanges);
         // console.log("userPlayer.position:", userPlayer.position);
         // console.log("aiPlayer.position:", aiPlayer.position);
         // console.log("this.state.raiseRanges[userPlayer.position]:", this.state.raiseRanges[userPlayer.position]);
         const handNotation = handToNotation(aiPlayer.hand);

         // TODO: we also need to set: 
         // this.updateStatePlayerPositions(player.position, player.status);
         // when the AI raises or 3-Bets

         // Possibilities:
         // AI raises, User 3-Bets, DONE (no AI 4-Bet or User 5-Betting)
         // User raises, AI 3-Bets, User 4-Bets, DONE
         // so:
         // AI only ever bets once.
         
         let action: PokerAction;
         
         let handData; // = positionRanges[handNotation];
         let frequencies: ActionsTotalFrequencies; // = handData.f;

         if (nobodyHasBetYet) {
            handData = positionRanges[handNotation];
            frequencies = handData.f;
         } else {
            // has to be an AI 3-Bet
            // assert(this.state.) TODO: confirm

            // should never get here as BB userPlayer

            const positionRaiseRanges = this.state.raiseRanges[userPlayer.position][aiPlayer.position]
            handData = positionRaiseRanges[handNotation];
            frequencies = handData.f;
         }
         
         if (this.state.difficultyMode === 'Simplified' && frequencies) {
            const cumulativeProb = {
               F: frequencies.F,
               C: frequencies.F + frequencies.C,
               R3:
                  frequencies.F +
                  frequencies.C +
                  (frequencies.R2 ?? 0) +
                  (frequencies["R2.3"] ?? 0) +
                  (frequencies["R2.5"] ?? 0) +
                  (frequencies.R3 ?? 0) +
                  (frequencies["R6.5"] ?? 0) +
                  (frequencies["R7.5"] ?? 0) +
                  (frequencies.R10 ?? 0) +
                  (frequencies.R11 ?? 0) +
                  (frequencies["R11.5"] ?? 0) +
                  (frequencies.R12 ?? 0) +
                  (frequencies["R12.3"] ?? 0) +
                  (frequencies.R13 ?? 0) +
                  (frequencies["R13.5"] ?? 0) +
                  (frequencies.R19 ?? 0) +
                  (frequencies.R18 ?? 0) +
                  (frequencies.R20 ?? 0) +
                  (frequencies.R22 ?? 0) +
                  (frequencies["R24.9"] ?? 0) +
                  (frequencies.R21 ?? 0) +
                  (frequencies.R25 ?? 0) +
                  (frequencies.R28 ?? 0) +
                  (frequencies.R26 ?? 0) +
                  (frequencies.R27 ?? 0) +
                  (frequencies.RAI ?? 0)
            };

            // Normalize the total to 1 if needed
            const total = cumulativeProb.R3;
            const rand = Math.random() * (total > 0 ? total : 1);

            // Select action based on the ranges
            if (rand < cumulativeProb.F) {
               action = 'F';
            } else if (rand < cumulativeProb.C) {
               action = 'C';
            } else {
               action = 'R3';
            }
         } else if (this.state.difficultyMode === 'Hard' && frequencies) {
            // TODO ... hard mode
            action = 'F';

         } else {
            console.warn("Frequencies undefined.");
            action = 'F';
         }
         
         // Update player state based on the selected action
         switch (action) {
            case 'F':
               aiPlayer.status = 'folded';
               break;
            case 'C':
               aiPlayer.status = 'called';
               // Match the highest bet
               this.state.potSize += (currentHighestBetAnyone - aiPlayer.betAmount);
               aiPlayer.betAmount = currentHighestBetAnyone;
               aiPlayer.chipStack -= aiPlayer.betAmount;
               break;
            case 'R3':
               aiPlayer.status = 'raised';
               // raise or 3-bet
               aiPlayer.betAmount = currentHighestBetAnyone * 3;
               this.state.potSize += (aiPlayer.betAmount - currentHighestBetAnyone);
               aiPlayer.chipStack -= aiPlayer.betAmount;
               break;
         }

         // TODO: we only need to add AI actions if:
         // an AI raises
         // an AI folds that previously raised? TODO?
         // console.log("AI action chosen: " + action);
         if (action === 'C' || action === 'R3') {
            this.state.playerActions.push({action, actor: 'AI'});
         }

      } else {
         // ai already bet, fold this hand
         aiPlayer.status = 'folded';
      }

      this.updateStatePlayerPositions(aiPlayer.position, aiPlayer.status);

      // Move to the next player
      this.moveToNextPlayer();

      if (this.shouldEndHand()) {
         this.endHand();
         return
      } else if (this.state.players[this.state.currentPlayerIndex].isUser) {
         // User's turn - determine correct actions
         this.determineCorrectUserActions();
      } else {
         // Another AI's turn
         setTimeout(() => this.performAIAction(), this.PLAYER_ACTION_SPEED_MS);
      }

      this.notifyStateChange("performAIAction()");
   }

   private moveToNextPlayer(): void {
      let nextIndex = (this.state.currentPlayerIndex + 1) % this.state.players.length;

      // Skip folded players
      while (
         this.state.players[nextIndex].status === 'folded' && 
            nextIndex !== this.state.currentPlayerIndex
      ) {
         nextIndex = (nextIndex + 1) % this.state.players.length;
      }

      this.state.currentPlayerIndex = nextIndex;
   }

   private shouldEndHand(): boolean {
      // Count active players
      const activePlayers = this.state.players.filter(
         p => p.status !== 'folded'
      );
      if (activePlayers.length <= 1) {
         return true;
      }

      const userPlayer = this.state.players.find(p => p.isUser);
      if (!userPlayer) { return true; }

      if (userPlayer.status === 'folded') {
         return true;
      }

      if (userPlayer.chipStack === 0) {
         return true; // TODO if doing card showdown, this will need to be removed, and the hand should continue through all AI
      }

      // Check if all active players have the same bet amount 
      // (everyone has acted and betting is complete)
      const bets = new Set(activePlayers.map(p => p.betAmount));
      return bets.size === 1;
   }

   private endHand(): void {
      console.log("endHand()");
      this.state.isHandComplete = true;
      this.notifyStateChange("endHand");
   }

   nextHandPlease() {
      if (!this.state) {
         console.error("NO this.state !");
      }
      if (!this.state.stats) {
         console.error("NO this.state.stats !");
      }
      // Check if we've reached the total number of rounds
      if (this.state.stats.roundNumber >= this.state.stats.roundsTotal) {
         // Dispatch an event that index.ts can listen for to show results
         document.dispatchEvent(new CustomEvent('show-results'));
         return;
      } else {
         // Increment the round number - do this before starting a new hand
         // to ensure the renderer detects the change
         this.state.stats.roundNumber++;
         console.log("Incrementing round number to:", this.state.stats.roundNumber);
         
         // Clear game state for the new hand
         this.state.playerActions = [];
         this.state.isHandComplete = false;
         
         // Start a new hand
         this.startNewHand();
      }
   }

   isHandComplete(): boolean {
      return this.state.isHandComplete;
   }

   setRoundsTotal(roundsTotal: number) {
      console.log("Setting roundsTotal: " + roundsTotal);
      this.state.stats.roundsTotal = roundsTotal;
   }

   setRoundNumber(roundNumber: number) {
      console.log("Setting roundNumber: " + roundNumber);
      this.state.stats.roundNumber = roundNumber;
   }

   incRoundNumber() {
      this.setRoundNumber(this.state.stats.roundNumber + 1);
   }

   resetRoundStats(roundsTotal: number) {
      console.log("Resetting Round Stats");
      // We set roundNumber to 0 so the first increment will make it 1
      this.state.stats.roundNumber = 0;
      this.state.stats.roundsTotal = roundsTotal;
      this.state.stats.totalActions = 0;
      this.state.stats.correctDecisions = 0;
   }

   getStats(): TrainingStats {
      return this.state.stats;
   }

   /**
    * Sets the AI player action speed in milliseconds
    * @param speedMs The action speed in milliseconds (10-1000)
    */
   setPlayerActionSpeed(speedMs: number): void {
      // Make sure the speed is in the valid range (10ms to 1000ms)
      if (speedMs >= 10 && speedMs <= 1000) {
         this.PLAYER_ACTION_SPEED_MS = speedMs;
      }
   }
   
   /**
    * Gets the current player action speed in milliseconds
    * @returns The current action speed in milliseconds
    */
   getPlayerActionSpeed(): number {
      return this.PLAYER_ACTION_SPEED_MS;
   }
   /**
    * Sets the hand range width percentage (0-100)
    * @param width The width percentage (0 = tight range, 100 = full range)
    */
   setHandRangeWidth(width: number): void {
      // Make sure the width is in the valid range (0-100)
      if (width >= 0 && width <= 100) {
         this.state.handRangeWidth = width;
      }
   }
   
   /**
    * Gets the current hand range width percentage
    * @returns The current hand range width (0-100)
    */
   getHandRangeWidth(): number {
      return this.state.handRangeWidth;
   }   
   /**
    * Sets the deal animation duration in milliseconds
    * @param durationMs The duration in milliseconds
    */
   setDealAnimationDuration(durationMs: number): void {
      if (durationMs >= 100 && durationMs <= DEAL_ANIMATION_MAX_MS) {
         this.dealAnimationDuration = durationMs;
      }
   }
   
   /**
    * Gets the current deal animation duration in milliseconds
    * @returns The current animation duration in milliseconds
    */
   getDealAnimationDuration(): number {
      return this.dealAnimationDuration;
   }
}
